import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Code2, Award, Handshake } from "lucide-react";
import { Reveal, Stagger, staggerItem } from "@/components/Motion";
import { motion } from "framer-motion";
import CtaBanner from "@/components/CtaBanner";
import Seo from "@/components/Seo";
import { IMAGES } from "@/lib/site-data";

export default function JobSeekers() {
  const tracks = [
    { code: "T1", title: "Cloud Engineering", body: "AWS, Azure, GCP. Architecture, networking, automation.", time: "14 weeks" },
    { code: "T2", title: "DevOps & Platform", body: "Kubernetes, Terraform, CI/CD, SRE fundamentals.", time: "12 weeks" },
    { code: "T3", title: "Data & AI/ML", body: "Python, SQL, Snowflake/Databricks, LLM workflows.", time: "16 weeks" },
    { code: "T4", title: "Cybersecurity", body: "Security fundamentals, cloud sec, compliance.", time: "14 weeks" },
  ];

  const stepsPath = [
    { Icon: Compass, t: "Discover", d: "Free career assessment — we map your background to the right track." },
    { Icon: Code2, t: "Train", d: "Live cohorts, hands-on labs, and 1:1 mentorship from senior engineers." },
    { Icon: Award, t: "Certify", d: "Certification-ready curriculum with exam prep and practice labs." },
    { Icon: Handshake, t: "Place", d: "Resume, interview prep, and direct introductions to hiring partners." },
  ];

  return (
    <div data-testid="page-job-seekers">
      <Seo
        title="Tech Training & Career Placement — AI, Cloud, DevOps, Cybersecurity | Houston TX & Washington DC"
        description="Live cohort-based tech training in AI, Cloud, DevOps, Data, Cybersecurity, and Project Management with placement support. Houston, Richmond, Sugar Land, Fulshear TX, Washington DC."
        path="/job-seekers"
      />
      <section className="relative bg-slate-950 text-white pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 grid-lines-dark opacity-60" />
        <div className="absolute -top-20 -left-20 w-[500px] h-[500px] bg-emerald-500/10 blur-3xl rounded-full" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal><div className="eyebrow text-emerald-400">For Job Seekers</div></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-5xl">
              Launch the tech career you actually want.
            </h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-8 max-w-3xl text-lg md:text-xl text-slate-300 leading-relaxed">
              Training that leads to real jobs. Cohort-based programs in cloud, DevOps, data, AI, and
              cybersecurity — followed by dedicated placement support with our network of 200+ hiring partners.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="jobseekers-start-cta" className="group inline-flex items-center gap-2 bg-emerald-500 text-slate-950 px-8 py-4 font-medium hover:bg-emerald-400 transition-colors">
                Start Your Tech Career <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />
              </Link>
              <Link to="/services#training" className="inline-flex items-center gap-2 border border-slate-700 text-white px-8 py-4 font-medium hover:border-emerald-500 hover:text-emerald-400 transition-colors">
                View curriculum
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Tracks */}
      <section className="py-28 md:py-36 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow text-emerald-600">Training tracks</div>
                <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                  Pick a specialization. Go deep.
                </h2>
              </Reveal>
            </div>
          </div>
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {tracks.map((t, i) => (
              <motion.div key={t.code} variants={staggerItem} className="group bg-slate-50 border border-slate-200 p-8 hover:border-slate-900 hover:bg-white hover:-translate-y-1 transition-all" data-testid={`track-${i}`}>
                <div className="font-mono text-xs tracking-widest text-emerald-600">{t.code} / TRACK</div>
                <h3 className="mt-5 font-display text-2xl font-semibold text-slate-900 tracking-tight">{t.title}</h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{t.body}</p>
                <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-widest">{t.time}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                </div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Placement path */}
      <section className="py-28 md:py-36 bg-slate-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="absolute inset-0 border border-emerald-500 translate-x-4 translate-y-4" />
                <img src={IMAGES.servicesTech} alt="Career pathway" className="relative w-full h-[520px] object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="eyebrow text-emerald-600">Placement support</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                Trained to get hired — not just certified.
              </h2>
              <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-xl">
                Every graduate works with a dedicated career coach through resume coaching, mock
                interviews, and direct introductions to our hiring network. 85% of our graduates land a
                tech role within 90 days of completing a track.
              </p>
            </Reveal>
            <Stagger className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {stepsPath.map((s, i) => (
                <motion.div key={s.t} variants={staggerItem} className="bg-white border border-slate-200 p-6">
                  <s.Icon className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                  <div className="mt-4 font-display text-lg font-semibold text-slate-900">
                    <span className="text-emerald-600 font-mono text-xs mr-2">0{i + 1}</span>{s.t}
                  </div>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.d}</p>
                </motion.div>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Start your tech career"
        title="Your next chapter starts with a conversation."
        body="Tell us your background and goals. We'll help you map the right track — no pressure, no obligation."
        primary={{ to: "/contact", label: "Start Your Tech Career" }}
      />
    </div>
  );
}
